/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect,useCallback} from 'react';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import SlidePanel from '../components/common/SlidePanel';
import DataTable from '../components/common/DataTable';
import Badge from '../components/common/Badge';
import {Input,Select} from '../components/common/FormField';
import api from '../services/api';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import {fmtDate,fmtDateTime} from '../utils/helpers';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';

// ─── constants ────────────────────────────────────────────────────────────────
const INR=(n:number)=>Number(n||0).toLocaleString('en-KE',{minimumFractionDigits:2});
const STATUS_CSS:Record<string,string>={ACTIVE:'bg-green-100 text-green-700',SCHEDULED:'bg-blue-100 text-[#02357A]',COMPLETED:'bg-teal-100 text-teal-700',CANCELLED:'bg-red-100 text-red-700',PENDING:'bg-amber-100 text-amber-700',IN_PROGRESS:'bg-purple-100 text-purple-700',ADMITTED:'bg-blue-100 text-[#02357A]',SUBMITTED:'bg-amber-100 text-amber-700',RESOLVED:'bg-green-100 text-green-700',PAID:'bg-green-100 text-green-700'};
const RELATIONSHIP=['SELF','SPOUSE','CHILD','PARENT','SIBLING','GUARDIAN','OTHER'];
const FEEDBACK_TYPES=['DOCTOR','NURSING','BILLING','FACILITY','OVERALL','COMPLAINT'];
const TELE_DEPT=['OPD','Cardiology','Dermatology','Orthopedics','Pediatrics','Gynecology','Psychiatry','General Medicine'];

const TIMELINE_ICON:Record<string,string>={APPOINTMENT:'📅',LAB_REPORT:'🧪',PRESCRIPTION:'💊',ADMISSION:'🏥',VITALS:'❤️',DIAGNOSIS:'🔍'};
const REPORT_STATUS:Record<string,string>={NORMAL:'text-green-600',ABNORMAL:'text-red-600',CRITICAL:'text-red-700 font-bold',PENDING:'text-amber-600'};

export default function PatientPortalPage(){
  const {t}=useTranslation();
  const [tab,setTab]=useState<'dashboard'|'appointments'|'reports'|'prescriptions'|'billing'|'documents'|'telemedicine'|'family'|'timeline'|'feedback'|'admin'>('dashboard');
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [saving,setSaving]=useState(false);

  // Portal data
  const [portalStats,setPortalStats]=useState<any>({});
  const [dashData,setDashData]=useState<any>({});
  const [appointments,setAppointments]=useState<any[]>([]);
  const [labReports,setLabReports]=useState<any[]>([]);
  const [radReports,setRadReports]=useState<any[]>([]);
  const [vitals,setVitals]=useState<any[]>([]);
  const [prescriptions,setPrescriptions]=useState<any[]>([]);
  const [diagnoses,setDiagnoses]=useState<any[]>([]);
  const [bills,setBills]=useState<any[]>([]);
  const [payments,setPayments]=useState<any[]>([]);
  const [documents,setDocuments]=useState<any[]>([]);
  const [admissionDetail,setAdmissionDetail]=useState<any>(null);
  const [familyMembers,setFamilyMembers]=useState<any[]>([]);
  const [feedback,setFeedback]=useState<any[]>([]);
  const [teleSessions,setTeleSessions]=useState<any[]>([]);
  const [timeline,setTimeline]=useState<any[]>([]);
  const [notifications,setNotifications]=useState<any[]>([]);
  const [unreadCount,setUnreadCount]=useState(0);

  // OTP Registration (admin can demo-register a patient)
  const [showRegisterForm,setShowRegisterForm]=useState(false);
  const [regStep,setRegStep]=useState<'form'|'otp'>('form');
  const [regForm,setRegF]=useState({mobile:'',uhid:'',name:'',email:''});
  const [regAccountId,setRegAccountId]=useState('');
  const [regOtp,setRegOtp]=useState('');
  const [regResult,setRegResult]=useState<any>(null);

  // Appointment booking
  const [showApptForm,setShowApptForm]=useState(false);
  const [apptForm,setAF]=useState({doctorId:'',date:'',time:'',department:'OPD',visitType:'OPD',chiefComplaint:''});

  // Feedback
  const [showFeedbackForm,setShowFeedbackForm]=useState(false);
  const [feedbackForm,setFF]=useState({feedbackType:'OVERALL',department:'',doctorName:'',rating:5,title:'',comments:'',isAnonymous:false});

  // Family member
  const [showFamilyForm,setShowFamilyForm]=useState(false);
  const [familyForm,setFamF]=useState({mobile:'',uhid:'',relationship:'SPOUSE',canViewReports:true,canBookAppointments:true,canMakePayments:false});

  // Telemedicine
  const [showTeleForm,setShowTeleForm]=useState(false);
  const [teleForm,setTF2]=useState({doctorName:'',department:'OPD',scheduledAt:'',durationMinutes:'15',consultationFee:'500',sessionType:'VIDEO'});

  // Notification drawer
  const [showNotifs,setShowNotifs]=useState(false);

  // Selected patient (for admin view)
  const [selPatient,setSelPatient]=useState<any>(null);
  const [isPortalAuthenticated,setIsPortalAuthenticated]=useState(false);
  const [portalToken,setPortalToken]=useState<string|null>(null);

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),5000);};

  useEffect(()=>{
    api.portalStats().then(r=>setPortalStats(r.data||{})).catch(()=>{});
  },[]);

  // Refresh portal data for selected patient
  const refreshPortalData=useCallback(async()=>{
    if(!isPortalAuthenticated)return;
    setLoading(true);
    try{
      const[d,n]=await Promise.all([
        api.portalDashboard().then(r=>r.data||{}).catch(()=>({})),
        api.portalGetNotifications().then(r=>r.data||[]).catch(()=>[]),
      ]);
      setDashData(d);
      setNotifications(n);
      setUnreadCount(n.filter((x:any)=>!x.isRead).length);
      if(tab==='appointments'){const r=await api.portalGetAppointments();setAppointments(r.data||[]);}
      if(tab==='reports'){const[l,ra,v]=await Promise.all([api.portalGetLabReports(),api.portalGetRadiologyReports(),api.portalGetVitals(10)]);setLabReports(l.data||[]);setRadReports(ra.data||[]);setVitals(v.data||[]);}
      if(tab==='prescriptions'){const[rx,dx]=await Promise.all([api.portalGetPrescriptions(),api.portalGetDiagnoses()]);setPrescriptions(rx.data||[]);setDiagnoses(dx.data||[]);}
      if(tab==='billing'){const[b,p]=await Promise.all([api.portalGetBills(),api.portalGetPayments()]);setBills(b.data||[]);setPayments(p.data||[]);}
      if(tab==='documents'){const r=await api.portalGetDocuments();setDocuments(r.data||[]);}
      if(tab==='telemedicine'){const r=await api.portalGetTelemedicine();setTeleSessions(r.data||[]);}
      if(tab==='family'){const r=await api.portalGetFamily();setFamilyMembers(r.data||[]);}
      if(tab==='feedback'){const r=await api.portalGetFeedback();setFeedback(r.data||[]);}
      if(tab==='timeline'){const r=await api.portalGetTimeline();setTimeline(r.data||[]);}
    }catch{}finally{setLoading(false);}
  },[isPortalAuthenticated,tab]);
  useEffect(()=>{refreshPortalData();},[refreshPortalData]);

  // Registration flow
  const handleRegister=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{
      const r=await api.portalRegister(regForm);
      setRegAccountId(r.data.accountId);setRegResult(r.data);setRegStep('otp');
      notify(`✅ OTP sent to ${r.data.mobile} (check server logs in demo mode)`);
    }catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const handleVerifyOtp=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{
      const r=await api.portalVerifyOtp({accountId:regAccountId,otp:regOtp,deviceType:'WEB'});
      // Store token for subsequent calls
      api['c'].defaults.headers.common['Authorization']=`Bearer ${r.data.access_token}`;
      setPortalToken(r.data.access_token);
      setSelPatient(r.data.patient);
      setIsPortalAuthenticated(true);
      setShowRegisterForm(false);setRegStep('form');
      notify('✅ Portal access granted! Welcome to the Patient Portal.');
    }catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const bookAppointment=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.portalBookAppointment(apptForm);notify('✅ Appointment booked!');setShowApptForm(false);const r=await api.portalGetAppointments();setAppointments(r.data||[]);}
    catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const submitFeedback=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.portalSubmitFeedback(feedbackForm);notify('✅ Feedback submitted!');setShowFeedbackForm(false);const r=await api.portalGetFeedback();setFeedback(r.data||[]);}
    catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const addFamilyMember=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.portalAddFamilyMember(familyForm);notify('✅ Family member added!');setShowFamilyForm(false);const r=await api.portalGetFamily();setFamilyMembers(r.data||[]);}
    catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const scheduleTelemedicine=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.portalScheduleTelemedicine({...teleForm,consultationFee:+teleForm.consultationFee,durationMinutes:+teleForm.durationMinutes});notify('✅ Video consultation scheduled!');setShowTeleForm(false);const r=await api.portalGetTelemedicine();setTeleSessions(r.data||[]);}
    catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const StarRating=({value,onChange}:{value:number,onChange:(n:number)=>void})=>(
    <div className="flex gap-1">{[1,2,3,4,5].map(n=><button key={n} type="button" onClick={()=>onChange(n)} className={`text-2xl ${n<=value?'text-amber-400':'text-slate-200'} hover:text-amber-400`}>★</button>)}</div>
  );

  const TABS=[['dashboard','🏠 Dashboard'],['appointments','📅 Appointments'],['reports','🧪 Reports'],['prescriptions','💊 Prescriptions'],['billing','💰 Billing'],['documents','📄 Documents'],['telemedicine','🎥 Telemedicine'],['family','👨‍👩‍👧 Family'],['timeline','📜 Timeline'],['feedback','⭐ Feedback'],['admin','⚙️ Admin']];

  return(
    <Layout title="Patient Portal" actions={
      <div className="flex gap-2 items-center">
        {isPortalAuthenticated&&<button onClick={()=>setShowNotifs(true)} className="relative btn-secondary text-sm">
          🔔 {unreadCount>0&&<span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{unreadCount}</span>}
        </button>}
        {!isPortalAuthenticated&&<button onClick={()=>setShowRegisterForm(true)} className="btn-primary text-sm">🔐 Patient Login / Register</button>}
        {isPortalAuthenticated&&<div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5"><span className="text-green-600 text-sm font-semibold">✅ {selPatient?.firstName||'Patient'}</span><button onClick={()=>{setIsPortalAuthenticated(false);setPortalToken(null);setSelPatient(null);setDashData({});}} className="text-xs text-red-400 hover:underline">Logout</button></div>}
      </div>
    }>
      {msg&&<div className={`mb-3 p-2.5 rounded-lg text-sm border ${msg.startsWith('✅')?'bg-green-50 text-green-700 border-green-200':'bg-red-50 text-red-700 border-red-200'}`}>{msg}</div>}

      {/* Admin stats (always visible) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard label="Portal Accounts" value={portalStats.totalAccounts||0} icon="👤" color="blue"/>
        <StatCard label="Active Today" value={portalStats.activeToday||0} icon="✅" color="green"/>
        <StatCard label="Appointments Today" value={portalStats.appointmentsToday||0} icon="📅" color="purple"/>
        <StatCard label="Pending Teleconsult" value={portalStats.pendingTelemedicine||0} icon="🎥" color="amber"/>
      </div>

      {/* Not authenticated state */}
      {!isPortalAuthenticated&&(
        <div className="card p-10 text-center space-y-4">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center text-4xl mx-auto">🏥</div>
          <h2 className="text-2xl font-bold text-slate-700">Patient Self-Service Portal</h2>
          <p className="text-slate-500 max-w-lg mx-auto">Patients can register or login using their mobile number / UHID to access appointments, reports, prescriptions, bills, and more.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mt-4">
            {[['📅','Book Appointments'],['🧪','View Lab Reports'],['💊','Prescriptions'],['💰','Bills & Payments'],['🎥','Telemedicine'],['📄','Download Docs'],['👨‍👩‍👧','Family Access'],['⭐','Feedback']].map(([i,l])=>(
              <div key={l as string} className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-2xl">{i as string}</p><p className="text-xs text-slate-500 mt-1">{l as string}</p></div>
            ))}
          </div>
          <button onClick={()=>setShowRegisterForm(true)} className="btn-primary text-lg px-8 py-3 mt-4">🔐 Login / Register as Patient</button>
        </div>
      )}

      {/* Authenticated portal */}
      {isPortalAuthenticated&&(<>
        {/* Patient profile header */}
        <div className="card p-4 mb-4 flex items-center gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-[#039089] flex items-center justify-center text-white font-black text-xl flex-shrink-0">
            {selPatient?.firstName?.[0]?.toUpperCase()||'P'}
          </div>
          <div className="flex-1">
            <p className="font-black text-xl">{selPatient?.firstName} {selPatient?.lastName||''}</p>
            <div className="flex gap-4 text-sm text-slate-500 mt-0.5 flex-wrap">
              <span>UHID: <span className="font-mono font-semibold text-[#02357A]">{selPatient?.uhid||'—'}</span></span>
              <span>DOB: {selPatient?.dateOfBirth?fmtDate(selPatient.dateOfBirth):'—'}</span>
              <span>Mobile: {selPatient?.mobile||'—'}</span>
              {dashData.currentAdmission&&<span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold text-xs">🏥 Currently Admitted</span>}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto pb-1 border-b">
          {TABS.map(([t,l])=><button key={t} onClick={()=>setTab(t as any)} className={`px-3 py-1.5 rounded-t-lg text-xs font-medium whitespace-nowrap flex-shrink-0 ${tab===t?'bg-[#02357A] text-white':'text-slate-600 hover:bg-slate-100'}`}>{l}</button>)}
        </div>

        {/* ════ DASHBOARD ════ */}
        {tab==='dashboard'&&(
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card p-4 text-center border-2 border-blue-200 bg-blue-50">
                <p className="text-3xl font-black text-[#02357A]">{dashData.upcomingAppointments?.length||0}</p>
                <p className="text-xs font-semibold text-[#02357A] mt-1">Upcoming Appts</p>
              </div>
              <div className="card p-4 text-center border-2 border-red-200 bg-red-50">
                <p className="text-3xl font-black text-red-600">{dashData.pendingBills?.count||0}</p>
                <p className="text-xs font-semibold text-red-700 mt-1">Pending Bills</p>
                {dashData.pendingBills?.total>0&&<p className="text-xs text-red-500">{getCurrencySymbol()}{INR(dashData.pendingBills.total)}</p>}
              </div>
              <div className="card p-4 text-center border-2 border-teal-200 bg-teal-50">
                <p className="text-3xl font-black text-[#039089]">{dashData.recentReports?.length||0}</p>
                <p className="text-xs font-semibold text-teal-700 mt-1">Recent Reports</p>
              </div>
              <div className="card p-4 text-center border-2 border-purple-200 bg-purple-50">
                <p className="text-3xl font-black text-purple-600">{dashData.recentPrescriptions?.length||0}</p>
                <p className="text-xs font-semibold text-purple-700 mt-1">Active Rx</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Upcoming appointments */}
              <div className="card overflow-hidden">
                <div className="p-4 border-b flex justify-between items-center">
                  <h3 className="font-semibold">📅 Upcoming Appointments</h3>
                  <button onClick={()=>setTab('appointments')} className="text-xs text-[#02357A] hover:underline">View all →</button>
                </div>
                {!dashData.upcomingAppointments?.length?<p className="p-4 text-sm text-slate-400">No upcoming appointments</p>:
                <div className="divide-y">{dashData.upcomingAppointments.map((a:any)=>(
                  <div key={a.id} className="p-3">
                    <div className="flex justify-between items-start"><p className="font-semibold text-sm">{a.department||'OPD'}</p><Badge label={a.status} className={STATUS_CSS[a.status]||''}/></div>
                    <p className="text-xs text-slate-500 mt-0.5">Dr. {a.doctor_name||'—'} · {fmtDate(a.appointment_date)} {a.appointment_time||''}</p>
                    <p className="font-mono text-xs text-[#02357A] mt-0.5">{a.appointment_number||a.appointment_number||a.appointmentNumber}</p>
                  </div>
                ))}</div>}
              </div>

              {/* Current vitals */}
              {dashData.latestVitals&&(
                <div className="card overflow-hidden">
                  <div className="p-4 border-b"><h3 className="font-semibold">❤️ Latest Vitals</h3><p className="text-xs text-slate-400">{fmtDateTime(dashData.latestVitals.recorded_at)}</p></div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[['BP',`${dashData.latestVitals.systolic_bp||'—'}/${dashData.latestVitals.diastolic_bp||'—'}mmHg`,'❤️'],['Temp',`${dashData.latestVitals.temperature||'—'}°C`,'🌡️'],['SpO2',`${dashData.latestVitals.spo2||'—'}%`,'💨'],['Pulse',`${dashData.latestVitals.pulse_rate||'—'} bpm`,'💓'],['Weight',`${dashData.latestVitals.weight_kg||'—'} kg`,'⚖️'],['RR',`${dashData.latestVitals.respiratory_rate||'—'}/min`,'🫁']].map(([l,v,i])=>(
                      <div key={l as string} className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-xl">{i as string}</p><p className="font-bold text-sm mt-1">{v as string}</p><p className="text-xs text-slate-400">{l as string}</p></div>
                    ))}
                  </div>
                </div>
              )}

              {/* IPD Admission */}
              {dashData.currentAdmission&&(
                <div className="card overflow-hidden border-2 border-blue-300">
                  <div className="p-4 border-b bg-blue-50"><h3 className="font-semibold text-[#02357A]">🏥 Current Admission</h3></div>
                  <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                    {[['Ward',dashData.currentAdmission.ward_name||'—'],['Bed',dashData.currentAdmission.bed_number||'—'],['Admitted',fmtDate(dashData.currentAdmission.admission_date)],['Status',dashData.currentAdmission.status]].map(([l,v])=>(
                      <div key={l as string} className="bg-blue-50 rounded p-2"><p className="text-xs text-slate-400">{l as string}</p><p className="font-semibold">{v as string}</p></div>
                    ))}
                  </div>
                  <div className="px-4 pb-4"><button onClick={()=>setTab('timeline')} className="btn-secondary text-xs w-full">View Progress →</button></div>
                </div>
              )}

              {/* Quick actions */}
              <div className="card p-4">
                <h3 className="font-semibold mb-3">⚡ Quick Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  {[['📅 Book Appointment',()=>{setTab('appointments');setShowApptForm(true);},'blue'],['🎥 Teleconsult',()=>{setTab('telemedicine');setShowTeleForm(true);},'purple'],['⭐ Feedback',()=>{setTab('feedback');setShowFeedbackForm(true);},'amber'],['👨‍👩‍👧 Add Family',()=>{setTab('family');setShowFamilyForm(true);},'green']].map(([l,fn,c])=>(
                    <button key={l as string} onClick={fn as any} className={`p-3 rounded-xl bg-${c}-50 border-2 border-${c}-200 text-${c}-700 text-sm font-semibold hover:bg-${c}-100 text-left`}>{l as string}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════ APPOINTMENTS ════ */}
        {tab==='appointments'&&(
          <div className="space-y-3">
            <div className="flex justify-end"><button onClick={()=>setShowApptForm(true)} className="btn-primary text-sm">📅 Book Appointment</button></div>
            <div className="card overflow-hidden">
              <DataTable loading={loading} emptyMessage="No appointments" emptyIcon="📅" data={appointments} columns={[
                {key:'no',label:'Appt #',render:(r:any)=><span className="font-mono text-xs text-[#02357A]">{r.appointment_number||r.appointment_number||r.appointmentNumber}</span>},
                {key:'date',label:'Date & Time',render:(r:any)=><div><p className="font-semibold text-sm">{fmtDate(r.appointment_date||r.appointment_date||r.appointmentDate)}</p><p className="text-xs text-slate-400">{r.appointment_time||r.appointmentTime||'—'}</p></div>},
                {key:'dept',label:'Department',render:(r:any)=><span className="text-sm">{r.department||'OPD'}</span>},
                {key:'doctor',label:'Doctor',render:(r:any)=><span className="text-sm">Dr. {r.doctor_name||r.doctor_first_name||'Doctor'}</span>},
                {key:'type',label:'Type',render:(r:any)=><Badge label={r.visit_type||r.visitType||'OPD'} className="bg-blue-50 text-[#02357A]"/>},
                {key:'status',label:'Status',render:(r:any)=><Badge label={r.status||'BOOKED'} className={STATUS_CSS[r.status]||''}/>},
                {key:'actions',label:'',render:(r:any)=>['BOOKED','CONFIRMED'].includes(r.status)&&<button onClick={async()=>{if(window.confirm('Cancel this appointment?')){await api.portalCancelAppointment(r.id,{reason:'Patient cancelled'});notify('✅ Cancelled');const res=await api.portalGetAppointments();setAppointments(res.data||[]);}}} className="text-xs text-red-400 hover:underline">{t('common.cancel','Cancel')}</button>},
              ]}/>
            </div>
          </div>
        )}

        {/* ════ REPORTS ════ */}
        {tab==='reports'&&(
          <div className="space-y-5">
            {/* Lab reports */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold">🧪 Lab Reports</h3></div>
              {loading?<p className="p-4 text-sm text-slate-400">Loading...</p>:labReports.length===0?<p className="p-4 text-sm text-slate-400">No lab reports found</p>:
              <div className="divide-y max-h-64 overflow-y-auto">{labReports.map((r:any)=>(
                <div key={r.id} className="p-3 flex items-start justify-between">
                  <div><p className="font-semibold text-sm">{r.test_name||r.testName||'Lab Test'}</p><p className="text-xs text-slate-400">{r.test_code||'—'} · {fmtDate(r.created_at||r.created_at||r.createdAt)}</p></div>
                  <div className="text-right"><p className={`text-sm font-bold ${REPORT_STATUS[r.result_status||r.resultStatus||'NORMAL']||''}`}>{r.result_value||r.resultValue||'—'} {r.unit||''}</p><p className="text-xs text-slate-400">{r.result_status||r.resultStatus||'—'}</p></div>
                </div>
              ))}</div>}
            </div>

            {/* Radiology reports */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold">📡 Radiology Reports</h3></div>
              {radReports.length===0?<p className="p-4 text-sm text-slate-400">No radiology reports found</p>:
              <div className="divide-y">{radReports.slice(0,10).map((r:any)=>(
                <div key={r.id} className="p-3 flex items-start justify-between">
                  <div><p className="font-semibold text-sm">{r.service_name||r.service_name||r.serviceName||'Radiology'}</p><p className="text-xs text-slate-400">{r.service_code||'—'} · {fmtDate(r.created_at)}</p></div>
                  <div className="flex gap-2 items-center"><Badge label={r.status||'COMPLETED'} className={STATUS_CSS[r.status]||''}/>{r.report_pdf_path&&<a href={r.report_pdf_path} target="_blank" rel="noreferrer" className="text-xs text-[#02357A] hover:underline">📥 PDF</a>}</div>
                </div>
              ))}</div>}
            </div>

            {/* Vitals trend */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold">❤️ Vitals History</h3></div>
              {vitals.length===0?<p className="p-4 text-sm text-slate-400">No vitals recorded</p>:
              <div className="divide-y max-h-56 overflow-y-auto">{vitals.map((v:any)=>(
                <div key={v.id} className="p-3 flex items-center gap-4 text-xs">
                  <span className="text-slate-400 flex-shrink-0">{fmtDate(v.recorded_at)}</span>
                  <div className="flex gap-4 flex-wrap">
                    {v.systolic_bp&&<span>BP: <strong>{v.systolic_bp}/{v.diastolic_bp}</strong></span>}
                    {v.temperature&&<span>Temp: <strong>{v.temperature}°C</strong></span>}
                    {v.spo2&&<span>SpO2: <strong>{v.spo2}%</strong></span>}
                    {v.pulse_rate&&<span>Pulse: <strong>{v.pulse_rate}bpm</strong></span>}
                    {v.weight_kg&&<span>Wt: <strong>{v.weight_kg}kg</strong></span>}
                  </div>
                </div>
              ))}</div>}
            </div>
          </div>
        )}

        {/* ════ PRESCRIPTIONS ════ */}
        {tab==='prescriptions'&&(
          <div className="space-y-5">
            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold">💊 Active Prescriptions</h3></div>
              {prescriptions.length===0?<p className="p-4 text-sm text-slate-400">No prescriptions found</p>:
              <div className="divide-y">{prescriptions.map((r:any)=>(
                <div key={r.id} className="p-3">
                  <div className="flex justify-between items-start">
                    <div><p className="font-semibold">{r.itemName||r.item_name||r.drug_name||'—'}</p><p className="text-xs text-slate-400">{r.dosage||'—'} · {r.frequency||'—'} · {r.duration||'—'}</p></div>
                    <div className="text-right"><Badge label={r.status||'ORDERED'} className={STATUS_CSS[r.status]||''}/><p className="text-xs text-slate-400 mt-0.5">{fmtDate(r.created_at)}</p></div>
                  </div>
                  {(r.doctor_name||r.doctorName)&&<p className="text-xs text-[#02357A] mt-1">👨‍⚕️ Dr. {r.doctor_name||r.doctorName}</p>}
                  {r.instructions&&<p className="text-xs text-slate-500 mt-1 bg-amber-50 px-2 py-1 rounded">📋 {r.instructions}</p>}
                </div>
              ))}</div>}
            </div>

            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold">🔍 Diagnosis History</h3></div>
              {diagnoses.length===0?<p className="p-4 text-sm text-slate-400">No diagnoses recorded</p>:
              <div className="divide-y">{diagnoses.map((d:any)=>(
                <div key={d.id} className="p-3 flex justify-between items-start">
                  <div><p className="font-semibold text-sm">{d.icd_description||d.icdDescription||d.diagnosis||'—'}</p><p className="text-xs text-slate-400">{d.icd_code||d.icdCode||'—'} · Dr. {d.doctor_name||'—'}</p></div>
                  <span className="text-xs text-slate-400">{fmtDate(d.created_at)}</span>
                </div>
              ))}</div>}
            </div>
          </div>
        )}

        {/* ════ BILLING ════ */}
        {tab==='billing'&&(
          <div className="space-y-5">
            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold">🧾 Bills</h3></div>
              <DataTable loading={loading} emptyMessage="No bills found" emptyIcon="💰" data={bills} columns={[
                {key:'no',label:'Bill #',render:(r:any)=><span className="font-mono text-xs">{r.billNumber||r.bill_number||r.id?.slice(-8)}</span>},
                {key:'date',label:'Date',render:(r:any)=><span className="text-xs">{fmtDate(r.billDate||r.bill_date||r.created_at||r.createdAt)}</span>},
                {key:'amount',label:'Amount',render:(r:any)=><span className="font-bold">{getCurrencySymbol()}{INR(+(r.totalAmount||r.total_amount||0))}</span>},
                {key:'paid',label:'Paid',render:(r:any)=><span className="text-green-600">{getCurrencySymbol()}{INR(+(r.paidAmount||r.paid_amount||0))}</span>},
                {key:'balance',label:'Balance',render:(r:any)=><span className={+(r.balanceDue||r.balance_due||0)>0?'text-red-600 font-bold':'text-slate-400'}>{getCurrencySymbol()}{INR(+(r.balanceDue||r.balance_due||0))}</span>},
                {key:'status',label:'Status',render:(r:any)=><Badge label={r.status||'PENDING'} className={STATUS_CSS[r.status]||''}/>},
              ]}/>
            </div>

            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold">💳 Payment History</h3></div>
              {payments.length===0?<p className="p-4 text-sm text-slate-400">No payments found</p>:
              <div className="divide-y">{payments.slice(0,10).map((p:any)=>(
                <div key={p.id} className="p-3 flex items-center justify-between">
                  <div><p className="font-semibold text-sm">{getCurrencySymbol()}{INR(+(p.amount||0))}</p><p className="text-xs text-slate-400">{p.paymentMode||p.payment_mode||'—'} · {p.referenceNumber||p.reference_number||'—'}</p></div>
                  <div className="text-right"><Badge label={p.status||'COMPLETED'} className={STATUS_CSS[p.status]||''}/><p className="text-xs text-slate-400 mt-0.5">{fmtDate(p.paymentDate||p.payment_date||p.createdAt)}</p></div>
                </div>
              ))}</div>}
            </div>
          </div>
        )}

        {/* ════ DOCUMENTS ════ */}
        {tab==='documents'&&(
          <div className="card overflow-hidden">
            {loading?<p className="p-4 text-sm text-slate-400">Loading...</p>:documents.length===0?<div className="p-8 text-center text-slate-400"><p className="text-3xl mb-2">📄</p><p>No documents found</p></div>:
            <div className="divide-y">{documents.map((d:any)=>(
              <div key={d.id} className="p-3 flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{d.icon||'📄'}</span>
                <div className="flex-1"><p className="font-semibold">{d.documentName||d.document_name||'Document'}</p><p className="text-xs text-slate-400">{d.category_name||d.categoryCode||'—'} · v{d.version||1} · {fmtDate(d.uploaded_at||d.uploadedAt)}</p></div>
                <div className="flex gap-2 items-center"><Badge label={d.status||'ACTIVE'} className={STATUS_CSS[d.status]||''}/>{d.filePath&&<a href={d.filePath} target="_blank" rel="noreferrer" className="text-xs text-[#02357A] hover:underline border border-blue-200 px-2 py-0.5 rounded">📥 Download</a>}</div>
              </div>
            ))}</div>}
          </div>
        )}

        {/* ════ TELEMEDICINE ════ */}
        {tab==='telemedicine'&&(
          <div className="space-y-4">
            <div className="flex justify-end"><button onClick={()=>setShowTeleForm(true)} className="btn-primary text-sm">🎥 Schedule Video Consultation</button></div>
            {teleSessions.length===0&&!loading&&<div className="card p-8 text-center text-slate-400"><p className="text-4xl mb-2">🎥</p><p>No telemedicine sessions scheduled</p></div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teleSessions.map(s=>(
                <div key={s.id} className={`card p-4 border-2 ${s.status==='SCHEDULED'?'border-blue-300':s.status==='IN_PROGRESS'?'border-green-400':s.status==='COMPLETED'?'border-teal-200':'border-slate-200'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div><p className="font-bold">Dr. {s.doctorName||s.doctor_name||'—'}</p><p className="text-sm text-slate-500">{s.department||'—'} · {s.sessionType||'VIDEO'}</p></div>
                    <Badge label={s.status} className={STATUS_CSS[s.status]||''}/>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div className="bg-slate-50 rounded p-2"><p className="text-slate-400">Scheduled</p><p className="font-semibold">{fmtDateTime(s.scheduledAt||s.scheduled_at)}</p></div>
                    <div className="bg-slate-50 rounded p-2"><p className="text-slate-400">Duration</p><p className="font-semibold">{s.duration_minutes||s.durationMinutes||15} min</p></div>
                    <div className="bg-slate-50 rounded p-2"><p className="text-slate-400">Fee</p><p className="font-semibold">{getCurrencySymbol()}{INR(+(s.consultation_fee||s.consultationFee||0))}</p></div>
                    <div className="bg-slate-50 rounded p-2"><p className="text-slate-400">Payment</p><Badge label={s.payment_status||s.paymentStatus||'PENDING'} className={STATUS_CSS[s.payment_status||s.paymentStatus]||''}/></div>
                  </div>
                  {s.status==='SCHEDULED'&&s.patientJoinUrl&&<a href={s.patientJoinUrl||s.patient_join_url} target="_blank" rel="noreferrer" className="btn-primary text-xs w-full text-center block">🎥 Join Consultation</a>}
                  {s.status==='COMPLETED'&&s.doctorNotes&&<p className="text-xs text-slate-500 bg-teal-50 rounded p-2 mt-2">📋 {s.doctorNotes||s.doctor_notes}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ════ FAMILY ════ */}
        {tab==='family'&&(
          <div className="space-y-4">
            <div className="flex justify-end"><button onClick={()=>setShowFamilyForm(true)} className="btn-secondary text-sm">+ Add Family Member</button></div>
            {familyMembers.length===0&&<div className="card p-8 text-center text-slate-400"><p className="text-4xl mb-2">👨‍👩‍👧</p><p>No family members linked</p></div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {familyMembers.map((m:any)=>(
                <div key={m.id} className="card p-4 flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">{(m.memberName||m.member_name||'M')[0]?.toUpperCase()}</div>
                  <div className="flex-1"><p className="font-bold">{m.memberName||m.member_name||m.patient?.firstName||'—'}</p><p className="text-xs text-slate-400">{m.relationship||'—'} · {m.patient?.mobile||m.mobile||'—'}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {(m.canViewReports||m.can_view_reports)&&<Badge label="View Reports" className="bg-blue-50 text-[#02357A] text-xs"/>}
                    {(m.canBookAppointments||m.can_book_appointments)&&<Badge label="Book Appts" className="bg-green-50 text-green-700 text-xs"/>}
                    {(m.canMakePayments||m.can_make_payments)&&<Badge label="Payments" className="bg-amber-50 text-amber-700 text-xs"/>}
                  </div></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ════ HEALTH TIMELINE ════ */}
        {tab==='timeline'&&(
          <div className="space-y-2">
            {loading?<p className="text-sm text-slate-400">Loading timeline...</p>:timeline.length===0?<div className="card p-8 text-center text-slate-400"><p className="text-3xl mb-2">📜</p><p>No health history to display</p></div>:
            <div className="relative"><div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200"/>
              <div className="space-y-3">{timeline.map((e:any,i:number)=>(
                <div key={i} className="flex items-start gap-4 pl-4">
                  <div className="w-8 h-8 rounded-full bg-white border-2 border-blue-400 flex items-center justify-center text-sm flex-shrink-0 z-10">{TIMELINE_ICON[e.type]||'📋'}</div>
                  <div className="card p-3 flex-1"><div className="flex justify-between items-start"><p className="font-semibold text-sm">{e.title||e.type}</p><span className="text-xs text-slate-400">{fmtDate(e.date)}</span></div>{e.ref&&<p className="font-mono text-xs text-[#02357A] mt-0.5">{e.ref}</p>}</div>
                </div>
              ))}</div>
            </div>}
          </div>
        )}

        {/* ════ FEEDBACK ════ */}
        {tab==='feedback'&&(
          <div className="space-y-4">
            <div className="flex justify-end"><button onClick={()=>setShowFeedbackForm(true)} className="btn-primary text-sm">⭐ Submit Feedback</button></div>
            {feedback.length===0&&<div className="card p-8 text-center text-slate-400"><p className="text-3xl mb-2">⭐</p><p>No feedback submitted yet</p></div>}
            <div className="space-y-3">
              {feedback.map((f:any)=>(
                <div key={f.id} className="card p-4">
                  <div className="flex justify-between items-start">
                    <div><p className="font-bold">{f.title||f.feedbackType||'Feedback'}</p><p className="text-sm text-slate-500">{f.feedbackType||'—'} · {f.department||'—'}</p></div>
                    <div className="text-right"><div className="text-amber-400">{'★'.repeat(f.rating||0)}{'☆'.repeat(5-(f.rating||0))}</div><Badge label={f.status||'SUBMITTED'} className={STATUS_CSS[f.status]||''}/></div>
                  </div>
                  {f.comments&&<p className="text-sm text-slate-600 mt-2">{f.comments}</p>}
                  {f.response&&<div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-[#02357A]"><span className="font-semibold">Hospital Response:</span> {f.response}</div>}
                  <p className="text-xs text-slate-400 mt-1">{fmtDate(f.createdAt||f.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ════ ADMIN ════ */}
        {tab==='admin'&&(
          <div className="space-y-4">
            <div className="card p-5">
              <h3 className="font-semibold mb-3">Patient Portal — Admin Overview</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {[['Total Accounts',portalStats.totalAccounts||0,'👤','blue'],['Active Today',portalStats.activeToday||0,'✅','green'],['Appointments Today',portalStats.appointmentsToday||0,'📅','purple'],['Pending Teleconsult',portalStats.pendingTelemedicine||0,'🎥','amber']].map(([l,v,i,c])=>(
                  <div key={l as string} className={`card p-4 text-center border-2 border-${c}-200 bg-${c}-50`}><p className="text-2xl">{i as string}</p><p className={`text-2xl font-black text-${c}-600 mt-1`}>{v as any}</p><p className={`text-xs text-${c}-700 mt-0.5`}>{l as string}</p></div>
                ))}
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold mb-3">Integration Points</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {[['OPD','Appointments, Token Queue'],['EMR','Prescriptions, Diagnoses'],['Lab','Test Reports'],['Radiology','Imaging Reports'],['Billing','Bills, Payments'],['DMS','Documents'],['Notifications','Alerts, Reminders'],['SHA','SHA claims']].map(([m,d])=>(
                    <div key={m as string} className="bg-white border rounded p-2"><p className="font-bold text-[#02357A]">{m as string}</p><p className="text-slate-400">{d as string}</p></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </>)}

      {/* ════ MODALS ════ */}

      {/* Register / Login Modal */}
      <SlidePanel isOpen={showRegisterForm} onClose={()=>{setShowRegisterForm(false);setRegStep('form');}} title={regStep==='form'?'Patient Portal — Login / Register':'Verify OTP'} size="sm">
        {regStep==='form'?(
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-[#02357A]">Enter your mobile number or UHID to access your health records.</div>
            <div><label className="label">Mobile Number *</label><Input type="tel" value={regForm.mobile} onChange={e=>setRegF({...regForm,mobile:e.target.value})} required placeholder="+2547XXXXXXXX"/></div>
            <div><label className="label">UHID (optional)</label><Input value={regForm.uhid} onChange={e=>setRegF({...regForm,uhid:e.target.value})} placeholder="Your hospital UHID"/></div>
            <div><label className="label">{t('common.name','Name')}</label><Input value={regForm.name} onChange={e=>setRegF({...regForm,name:e.target.value})}/></div>
            <div><label className="label">{t('common.email','Email')}</label><Input type="email" value={regForm.email} onChange={e=>setRegF({...regForm,email:e.target.value})}/></div>
            <button type="submit" disabled={saving} className="btn-primary w-full text-base">{saving?'Sending OTP...':'Send OTP →'}</button>
          </form>
        ):(
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700">OTP sent to {regResult?.mobile}. Check server logs in demo mode.</div>
            <div><label className="label">Enter 6-digit OTP *</label><Input value={regOtp} onChange={e=>setRegOtp(e.target.value)} maxLength={6} required placeholder="000000" className="text-center text-2xl tracking-widest font-mono"/></div>
            <div className="flex gap-3"><button type="submit" disabled={saving||regOtp.length<6} className="btn-primary flex-1">{saving?'Verifying...':'✅ Verify & Login'}</button><button type="button" onClick={()=>setRegStep('form')} className="btn-secondary">{t('common.back','Back')}</button></div>
          </form>
        )}
      </SlidePanel>

      {/* Book Appointment */}
      <SlidePanel isOpen={showApptForm} onClose={()=>setShowApptForm(false)} title="Book Appointment" size="md">
        <form onSubmit={bookAppointment} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Department *</label><Select value={apptForm.department} onChange={e=>setAF({...apptForm,department:e.target.value})} required>{['OPD','Cardiology','Dermatology','Orthopedics','Pediatrics','Gynecology','ENT','Ophthalmology','Neurology','General Medicine'].map(d=><option key={d}>{d}</option>)}</Select></div>
            <div><label className="label">Visit Type</label><Select value={apptForm.visitType} onChange={e=>setAF({...apptForm,visitType:e.target.value})}><option>OPD</option><option>TELECONSULT</option><option>FOLLOW_UP</option></Select></div>
            <div><label className="label">Date *</label><Input type="date" value={apptForm.date} onChange={e=>setAF({...apptForm,date:e.target.value})} required min={new Date().toISOString().split('T')[0]}/></div>
            <div><label className="label">Time *</label><Select value={apptForm.time} onChange={e=>setAF({...apptForm,time:e.target.value})} required><option value="">Select slot</option>{['09:00','09:30','10:00','10:30','11:00','11:30','12:00','14:00','14:30','15:00','15:30','16:00','16:30','17:00'].map(t=><option key={t}>{t}</option>)}</Select></div>
          </div>
          <div><label className="label">Chief Complaint</label><Input value={apptForm.chiefComplaint} onChange={e=>setAF({...apptForm,chiefComplaint:e.target.value})} placeholder="e.g. Fever and headache for 3 days"/></div>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Booking...':'📅 Book Appointment'}</button><button type="button" onClick={()=>setShowApptForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>

      {/* Schedule Telemedicine */}
      <SlidePanel isOpen={showTeleForm} onClose={()=>setShowTeleForm(false)} title="Schedule Video Consultation" size="md">
        <form onSubmit={scheduleTelemedicine} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="label">Doctor Name *</label><Input value={teleForm.doctorName} onChange={e=>setTF2({...teleForm,doctorName:e.target.value})} required placeholder="Dr. Sharma"/></div>
            <div><label className="label">{t('common.department','Department')}</label><Select value={teleForm.department} onChange={e=>setTF2({...teleForm,department:e.target.value})}>{TELE_DEPT.map(d=><option key={d}>{d}</option>)}</Select></div>
            <div><label className="label">Session Type</label><Select value={teleForm.sessionType} onChange={e=>setTF2({...teleForm,sessionType:e.target.value})}><option>VIDEO</option><option>AUDIO</option><option>CHAT</option></Select></div>
            <div><label className="label">Date & Time *</label><Input type="datetime-local" value={teleForm.scheduledAt} onChange={e=>setTF2({...teleForm,scheduledAt:e.target.value})} required/></div>
            <div><label className="label">Duration (min)</label><Select value={teleForm.durationMinutes} onChange={e=>setTF2({...teleForm,durationMinutes:e.target.value})}>{['10','15','20','30'].map(d=><option key={d}>{d}</option>)}</Select></div>
            <div><label className="label">Consultation Fee ({getCurrencySymbol()})</label><Input type="number" value={teleForm.consultationFee} onChange={e=>setTF2({...teleForm,consultationFee:e.target.value})}/></div>
          </div>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Scheduling...':'🎥 Schedule Consultation'}</button><button type="button" onClick={()=>setShowTeleForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>

      {/* Feedback */}
      <SlidePanel isOpen={showFeedbackForm} onClose={()=>setShowFeedbackForm(false)} title="Submit Feedback" size="md">
        <form onSubmit={submitFeedback} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Feedback Type *</label><Select value={feedbackForm.feedbackType} onChange={e=>setFF({...feedbackForm,feedbackType:e.target.value})} required>{FEEDBACK_TYPES.map(t=><option key={t}>{t}</option>)}</Select></div>
            <div><label className="label">{t('common.department','Department')}</label><Input value={feedbackForm.department} onChange={e=>setFF({...feedbackForm,department:e.target.value})}/></div>
          </div>
          <div><label className="label">Rating *</label><StarRating value={feedbackForm.rating} onChange={n=>setFF({...feedbackForm,rating:n})}/></div>
          <div><label className="label">Title</label><Input value={feedbackForm.title} onChange={e=>setFF({...feedbackForm,title:e.target.value})} placeholder="Brief summary"/></div>
          <div><label className="label">{t('common.comments','Comments')}</label><textarea className="input" rows={4} value={feedbackForm.comments} onChange={e=>setFF({...feedbackForm,comments:e.target.value})} placeholder="Your detailed feedback..."/></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={feedbackForm.isAnonymous} onChange={e=>setFF({...feedbackForm,isAnonymous:e.target.checked})} className="w-4 h-4"/><span>Submit anonymously</span></label>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Submitting...':'⭐ Submit Feedback'}</button><button type="button" onClick={()=>setShowFeedbackForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>

      {/* Add Family Member */}
      <SlidePanel isOpen={showFamilyForm} onClose={()=>setShowFamilyForm(false)} title="Add Family Member" size="sm">
        <form onSubmit={addFamilyMember} className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-[#02357A]">The family member must be already registered at the hospital.</div>
          <div><label className="label">Mobile Number *</label><Input type="tel" value={familyForm.mobile} onChange={e=>setFamF({...familyForm,mobile:e.target.value})} required/></div>
          <div><label className="label">UHID (optional)</label><Input value={familyForm.uhid} onChange={e=>setFamF({...familyForm,uhid:e.target.value})}/></div>
          <div><label className="label">Relationship *</label><Select value={familyForm.relationship} onChange={e=>setFamF({...familyForm,relationship:e.target.value})} required>{RELATIONSHIP.map(r=><option key={r}>{r}</option>)}</Select></div>
          <div className="space-y-2">
            {[['canViewReports','Can view reports'],['canBookAppointments','Can book appointments'],['canMakePayments','Can make payments']].map(([k,l])=>(
              <label key={k as string} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={(familyForm as any)[k as string]} onChange={e=>setFamF({...familyForm,[k as string]:e.target.checked})} className="w-4 h-4"/><span>{l as string}</span></label>
            ))}
          </div>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Adding...':'Add Family Member'}</button><button type="button" onClick={()=>setShowFamilyForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>

      {/* Notifications drawer */}
      <SlidePanel isOpen={showNotifs} onClose={()=>setShowNotifs(false)} title="Notifications" size="sm">
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {notifications.length===0&&<p className="p-4 text-sm text-slate-400 text-center">No notifications</p>}
          {notifications.map((n:any)=>(
            <div key={n.id} className={`p-3 rounded-lg flex items-start gap-3 cursor-pointer hover:bg-slate-50 ${!n.isRead?'bg-blue-50 border-l-2 border-blue-400':''}`} onClick={()=>api.portalMarkNotifRead(n.id).then(()=>setNotifications(prev=>prev.map(x=>x.id===n.id?{...x,isRead:true}:x)))}>
              <span className="text-xl flex-shrink-0">{n.icon||'🔔'}</span>
              <div className="flex-1"><p className={`text-sm ${!n.isRead?'font-semibold':''}`}>{n.title}</p><p className="text-xs text-slate-500 mt-0.5">{n.message}</p><p className="text-xs text-slate-400 mt-0.5">{fmtDateTime(n.created_at||n.createdAt)}</p></div>
            </div>
          ))}
        </div>
        {notifications.some((n:any)=>!n.isRead)&&<button onClick={()=>api.portalMarkAllRead().then(()=>setNotifications(prev=>prev.map(n=>({...n,isRead:true})))).then(()=>setUnreadCount(0))} className="btn-secondary text-xs w-full mt-3">Mark all as read</button>}
      </SlidePanel>
    </Layout>
  );
}
